import React from 'react';
import ModalsContainer from './ModalsContainer';

/**
 * Wires SprintDetail state/actions into ModalsContainer.
 */
const SprintDetailModals = ({
  isLoadingContent = false,
  isMultiCampaignMode,
  localSprint,
  allSprints,
  onSelectSprint,
  state,
  actions,
  handlers,
  modals,
  nested,
  wrappedToolbar,
  handleDeleteModalConfirm,
  handleTrashNestedSprint,
  renderHandlers,
  variableCampaign,
  handleCampaignVariablesChange,
  handleInheritedVariablePatch,
  closeMission,
  setMissionDocStack,
  restoredDocStack,
  skipSurfaceAnimation = false,
  consumeSkipSurfaceAnimation,
  tabOpenMissionId = null,
  missionDocDirect = false,
  lockStates,
  inProgressLockWarningModal,
  renameFieldModal,
  setRenameFieldModal,
  tasks,
  statuses,
}) => {
  // During tab load, only trust the tab's surface — never paint the previous
  // tab's mission overlay under/over the content loader.
  const detailSidebarTaskId = isLoadingContent
    ? tabOpenMissionId
    : state.detailSidebarTaskId;

  // Always feed the active tab's stack when that tab has a mission surface.
  // Gating on local/tab id equality briefly produced [] mid-switch, which
  // unmounted MissionDocSheet (activeDocId → null) and flashed the skeleton.
  const missionDocStack = tabOpenMissionId ? restoredDocStack : [];

  return (
    <ModalsContainer
    blockedModalState={modals.blockedModalState}
    onBlockedModalClose={modals.handleBlockedModalClose}
    onBlockedConfirm={modals.handleBlockedConfirm}
    deleteModalState={modals.deleteModalState}
    onDeleteModalConfirm={handleDeleteModalConfirm}
    onDeleteModalClose={modals.handleDeleteModalClose}
    deleteAllModalState={modals.deleteAllModalState}
    onDeleteAllModalConfirm={modals.handleDeleteAllModalConfirm}
    onDeleteAllModalClose={modals.handleDeleteAllModalClose}
    resetCampaignModalState={modals.resetCampaignModalState}
    onResetCampaignModalConfirm={modals.handleResetCampaignModalConfirm}
    onResetCampaignModalClose={modals.handleResetCampaignModalClose}
    dateValidationError={state.dateValidationError}
    onDateValidationErrorClose={() => state.setDateValidationError(null)}
    tasks={tasks}
    isArtifactSummaryOpen={state.isArtifactSummaryOpen}
    onArtifactSummaryClose={() => state.setIsArtifactSummaryOpen(false)}
    detailSidebarTaskId={detailSidebarTaskId}
    onDetailSidebarClose={closeMission}
    missionDocStack={missionDocStack}
    missionDocDirect={missionDocDirect}
    onMissionDocStackChange={setMissionDocStack}
    skipSurfaceAnimation={skipSurfaceAnimation}
    onSkipSurfaceAnimationConsumed={consumeSkipSurfaceAnimation}
    attachmentsModalTaskId={state.attachmentsModalTaskId}
    onAttachmentsModalClose={() => state.setAttachmentsModalTaskId(null)}
    onAttachmentsChange={renderHandlers.onAttachmentsChange}
    deliverablesModalTaskId={state.deliverablesModalTaskId}
    onDeliverablesModalClose={() => state.setDeliverablesModalTaskId(null)}
    onAddDeliverable={renderHandlers.onAddDeliverable}
    onRemoveDeliverable={renderHandlers.onRemoveDeliverable}
    onUpdateDeliverable={renderHandlers.onUpdateDeliverable}
    onEditDeliverables={(taskId) => state.setDeliverablesModalTaskId(taskId)}
    allCategories={actions.getAllCategories()}
    allTags={actions.getAllTags ? actions.getAllTags() : []}
    statuses={statuses}
    lockStates={lockStates}
    nestedSprints={nested.nestedSprints}
    allSprints={allSprints}
    currentView={wrappedToolbar.currentView}
    flowchartRef={state.flowchartRef}
    onStatusChangeWithModal={modals.handleStatusChangeWithModal}
    onPriorityChange={actions.handlePriorityChange}
    onDifficultyChange={actions.handleDifficultyChange}
    onDueDateChange={actions.handleDueDateChange}
    onRemindersChange={actions.handleRemindersChange}
    onEstimatedDurationMinutesChange={actions.handleEstimatedDurationMinutesChange}
    onCreatedAtChange={actions.handleCreatedAtChange}
    onStartedAtChange={actions.handleStartedAtChange}
    onCompletedAtChange={actions.handleCompletedAtChange}
    onUpdatedAtChange={actions.handleUpdatedAtChange}
    onCategoryChange={actions.handleCategoryChange}
    onRenameTask={actions.handleRenameTask}
    onIconChange={actions.handleIconChange}
    onAddStatus={handlers.handleAddStatus}
    onDeleteStatus={handlers.handleDeleteStatus}
    onRenameStatus={handlers.handleRenameStatus}
    onSetStatusAutoSync={actions.handleSetStatusAutoSync}
    onAddTag={actions.handleAddTag}
    onRemoveTag={actions.handleRemoveTag}
    onSetPrimaryTag={actions.handleSetPrimaryTag}
    onRenameTag={actions.handleRenameTag}
    onDeleteTag={actions.handleDeleteTag}
    onOpenNestedSprint={nested.handleOpenNestedSprint}
    onRemoveNestedSprint={() => {
      if (state.detailSidebarTaskId) {
        // Trash the nested campaign but keep the mission (unlink only)
        handleTrashNestedSprint(state.detailSidebarTaskId);
      }
    }}
    onDeleteTask={(taskId) => {
      if (taskId) {
        actions.handleDeleteTask(taskId);
      }
    }}
    onMissionDocTicksChange={actions.handleMissionDocTicksChange}
    onMissionDocFilesChange={actions.handleMissionDocFilesChange}
    onMissionVariablesChange={actions.handleMissionVariablesChange}
    onAttachedDocsChange={actions.handleAttachedDocsChange}
    onQuickNotesChange={actions.handleQuickNotesChange}
    onMissionDocsMigrated={actions.handleMissionDocsMigrated}
    onClearMissionLegacyNotes={actions.handleClearMissionLegacyNotes}
    variableCampaign={variableCampaign}
    onCampaignVariablesChange={handleCampaignVariablesChange}
    onPatchInheritedVariable={handleInheritedVariablePatch}
    inProgressLockWarningModal={inProgressLockWarningModal}
    isCustomFieldsManagerOpen={state.isCustomFieldsManagerOpen}
    onCustomFieldsManagerClose={() => state.setIsCustomFieldsManagerOpen(false)}
    customFields={localSprint?.customFields || []}
    onCustomFieldsChange={renderHandlers.onCustomFieldsChange}
    onCustomFieldValueChange={actions.handleCustomFieldValueChange}
    onConditionsChange={actions.handleConditionsChange}
    renameFieldModal={renameFieldModal}
    onRenameFieldModalClose={() => setRenameFieldModal(null)}
    breadcrumbPath={isMultiCampaignMode ? [] : nested.breadcrumbPath}
    currentCampaign={localSprint ? { id: localSprint.id, name: localSprint.name, icon: localSprint.icon } : null}
    onSelectSprint={onSelectSprint}
    deliverableSubmitModal={renderHandlers.deliverableSubmitModal}
    onDeliverableSubmitModalClose={() => state.setDeliverableSubmitModal(null)}
    onSubmitDeliverableLink={renderHandlers.onSubmitDeliverableLink}
    onSubmitDeliverableAttachment={renderHandlers.onSubmitDeliverableAttachment}
    onClearDeliverable={renderHandlers.onClearDeliverable}
  />
  );
};

export default SprintDetailModals;
